import { defineFeature } from '../define-feature';
import { SeoSpaceTab, seoSpaceWebPath } from '../../seo/seo-space-routes';

export default defineFeature({
  id: 'marketing.seo_pack.articles',
  category: 'marketing',
  title: 'Relire et valider vos articles',
  intent: "Relire les articles rédigés pour vous et les valider pour qu'ils soient publiés au prochain créneau.",
  context: {
    why: "Chaque article est rédigé à partir d'un brief construit sur ce que Google montre déjà pour la requête, écrit avec la voix que vous avez choisie, puis passé au contrôle qualité (aucun chiffre non sourcé, aucun lien inventé, mots interdits respectés). Il est construit pour être repris aussi bien par Google que par les assistants IA : la réponse arrive dès l'introduction, et l'article se termine par des questions fréquentes affichées en accordéon et balisées pour les machines. Il arrive chez vous en brouillon : vous relisez, vous retouchez si besoin, vous validez. Il prend alors le prochain créneau libre de vos jours de publication.",
    use_cases: [
      "Valider en un clic les articles du mois depuis l'e-mail « Vos articles sont prêts ».",
      'Corriger une tournure ou un détail avant publication.',
      'Compléter les questions fréquentes avec une question que vos clients vous posent vraiment.',
    ],
  },
  required_scopes: ['website.seo_articles'],
  prerequisites: [
    {
      ref: 'marketing.seo_pack.topics',
      requirement: 'required',
      why: 'Un article est rédigé pour chaque sujet du lot que vous avez lancé.',
    },
  ],
  ui_entry_points: [
    {
      route: seoSpaceWebPath(SeoSpaceTab.ARTICLES),
      element: 'Onglet « Articles », section « À relire » ; chaque article s’ouvre dans l’éditeur du blog',
    },
  ],
  api: {
    primary_operation_id: 'SeoContentAppController_validate',
    related_operation_ids: [
      'SeoContentAppController_timeline',
      'SeoContentAppController_pack',
      'ProNewsAppController_get',
      'ProNewsAppController_list',
    ],
  },
  steps: [
    '**Vous recevez « Vos articles sont prêts »** (e-mail, cloche, application mobile) : le bandeau du blog indique le nombre d’articles à relire.',
    '**Ouvrez chaque article** : il est en brouillon dans votre blog, avec son image et sa note de référencement sur 100.',
    "**Retouchez si besoin**, dans l'éditeur habituel du blog.",
    '**Validez** : l’article est programmé au prochain créneau libre de vos jours et heure de publication. Il se publie tout seul, avec ses questions fréquentes et son balisage.',
  ],
  gotchas: [
    "**Un article non validé n'est jamais publié** : la date qui lui était réservée reste vide si vous ne validez pas à temps.",
    "**Un article peut manquer à l'appel.** Chaque article passe notre contrôle qualité avant de vous être soumis : s'il est refusé, il est réécrit de zéro ; s'il est encore refusé, il ne vous est pas proposé, la date reste vide et notre équipe est prévenue. Vous ne voyez jamais un article refusé, et un article sauvé plus tard arrive dans un e-mail à part.",
    "**Déprogrammer ou dépublier un article** le ramène dans « À relire », prêt à reprendre un créneau ; supprimer la publication l'écarte : il ne pourra plus être validé.",
    '**La publication suit vos réglages** (jours et heure, deux jours par semaine par défaut) : valider un article ne le publie pas immédiatement.',
    '**Les effets demandent du temps** : comptez plusieurs semaines après publication avant que Google et les assistants IA aient lu et pris en compte un article, et deux à trois mois pour un effet stable. Le suivi des résultats par article arrivera dans les statistiques de votre site.',
    "**Gardez la section « Questions fréquentes »** si vous retouchez l'article : c'est elle que les assistants IA et Google lisent pour répondre directement à une question de client.",
    "**Vous pouvez tout modifier après publication** depuis le blog, comme n'importe quel article.",
  ],
  related_features: ['marketing.seo_pack.topics', 'marketing.seo_pack.overview', 'marketing.blog.article.create'],
});
